<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Change Log"
      subtitle="Global audit feed (HR profile, compensation, time-off, recycle actions only — Settings CRUD is not audited yet)"
      icon="pi-history"
    >
      <template #actions>
        <Button
          label="Export CSV"
          icon="pi pi-download"
          size="small"
          severity="secondary"
          outlined
          :disabled="!entries.length || exporting"
          :loading="exporting"
          @click="exportCsv"
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadEntries"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body">
      <SettingsChangelogFiltersBar
        v-model:search="filters.search"
        v-model:action="filters.action"
        v-model:entity-type="filters.entity_type"
        v-model:date-from="filters.date_from"
        v-model:date-to="filters.date_to"
        :can-view-compensation="canViewCompensation"
        @apply="applyFilters"
        @clear="clearFilters"
      />

      <p v-if="!loading && total > 0" class="settings-changelog-results">
        {{ total }} {{ total === 1 ? 'entry' : 'entries' }}
        <span v-if="filtersActive"> matching filters</span>
      </p>

      <div v-if="loading && !entries.length" class="settings-org-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-org-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadEntries" />
        </div>
      </Message>

      <div
        v-else-if="!entries.length"
        class="settings-org-section__empty"
      >
        <i class="pi pi-history settings-org-section__empty-icon" aria-hidden="true" />
        <p>{{ emptyMessage }}</p>
        <Button
          v-if="filtersActive"
          label="Clear filters"
          size="small"
          severity="secondary"
          outlined
          class="mt-2"
          @click="clearFilters"
        />
      </div>

      <template v-else>
        <DataTable
          :value="entries"
          size="small"
          class="settings-changelog-table"
          dataKey="id"
          sort-mode="single"
          removable-sort
        >
          <Column field="created_at" header="When" sortable style="width: 11rem">
            <template #body="{ data }">
              <span class="text-xs whitespace-nowrap">{{ data.created_at }}</span>
            </template>
          </Column>
          <Column field="actor_name" header="Actor" sortable>
            <template #body="{ data }">
              {{ data.actor_name || 'System' }}
            </template>
          </Column>
          <Column field="target_label" header="Employee" sortable>
            <template #body="{ data }">
              {{ data.target_label || '—' }}
            </template>
          </Column>
          <Column field="action" header="Action" sortable>
            <template #body="{ data }">
              {{ auditActionLabel(data.action) }}
            </template>
          </Column>
          <Column field="field_name" header="Field">
            <template #body="{ data }">
              {{ auditFieldLabel(data) }}
            </template>
          </Column>
          <Column header="Change">
            <template #body="{ data }">
              <div class="text-xs min-w-0 settings-changelog-change">
                <template v-if="auditChangeIsSingleValue(data)">
                  <span class="text-surface-800 dark:text-surface-200 break-all">
                    {{ formatAuditChangeOld(data) !== '—' ? formatAuditChangeOld(data) : formatAuditChangeNew(data) }}
                  </span>
                </template>
                <template v-else>
                  <span class="text-surface-500 line-through break-all">{{ formatAuditChangeOld(data) }}</span>
                  <i class="pi pi-arrow-right text-[0.6rem] mx-1.5 text-surface-400" />
                  <span class="text-surface-800 dark:text-surface-200 break-all">{{ formatAuditChangeNew(data) }}</span>
                </template>
              </div>
            </template>
          </Column>
        </DataTable>

        <div v-if="total > entries.length" class="flex justify-center mt-3">
          <Button
            label="Load more"
            icon="pi pi-chevron-down"
            size="small"
            severity="secondary"
            outlined
            :loading="loadingMore"
            @click="loadMore"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import { useAuth } from '@/composables/useAuth';
import { fetchGlobalAudit } from '@/api/auditApi';
import { isValidDateRange } from '@/constants/attendance';
import { downloadCsv } from '@/utils/fileDownload';
import {
  auditActionLabel,
  auditChangeIsSingleValue,
  auditFieldLabel,
  displayAuditValue,
  formatAuditChangeNew,
  formatAuditChangeOld,
} from '@/utils/auditDisplayUtils';
import SettingsChangelogFiltersBar from '../SettingsChangelogFiltersBar.vue';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import { hasActiveChangelogFilters } from '../../constants/changelogFilters';

const toast = useToast();
const { isSuperAdmin, hasPermission } = useAuth();
const pageSize = 50;

const entries = ref([]);
const total = ref(0);
const loading = ref(false);
const loadingMore = ref(false);
const exporting = ref(false);
const error = ref(null);

const canViewCompensation = computed(
  () => isSuperAdmin.value || hasPermission('compensation.view_all')
);

const filters = reactive({
  search: '',
  action: null,
  entity_type: null,
  date_from: '',
  date_to: '',
});

const activeFilters = reactive({
  search: '',
  action: null,
  entity_type: null,
  date_from: '',
  date_to: '',
});

const filtersActive = computed(() => hasActiveChangelogFilters(activeFilters));

const emptyMessage = computed(() =>
  filtersActive.value
    ? 'No entries match the current filters.'
    : 'No change log entries yet.'
);

function syncActiveFilters() {
  activeFilters.search = filters.search.trim();
  activeFilters.action = filters.action || null;
  activeFilters.entity_type = filters.entity_type || null;
  activeFilters.date_from = filters.date_from || '';
  activeFilters.date_to = filters.date_to || '';
}

function buildRequestParams(offset = 0) {
  const params = { limit: pageSize, offset };
  if (activeFilters.search) params.search = activeFilters.search;
  if (activeFilters.action) params.action = activeFilters.action;
  if (activeFilters.entity_type) params.entity_type = activeFilters.entity_type;
  if (activeFilters.date_from) params.date_from = activeFilters.date_from;
  if (activeFilters.date_to) params.date_to = activeFilters.date_to;
  return params;
}

async function loadEntries() {
  loading.value = true;
  error.value = null;
  try {
    const data = await fetchGlobalAudit(buildRequestParams(0));
    entries.value = data.entries;
    total.value = data.total;
  } catch (err) {
    error.value = err?.message || 'Failed to load change log.';
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  if (loadingMore.value || entries.value.length >= total.value) return;
  loadingMore.value = true;
  try {
    const data = await fetchGlobalAudit(buildRequestParams(entries.value.length));
    entries.value = entries.value.concat(data.entries);
    total.value = data.total;
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Load failed',
      detail: err?.message || 'Could not load more entries.',
      life: 3500,
    });
  } finally {
    loadingMore.value = false;
  }
}

function applyFilters() {
  if (filters.date_from && filters.date_to && !isValidDateRange(filters.date_from, filters.date_to)) {
    toast.add({
      severity: 'warn',
      summary: 'Invalid date range',
      detail: 'Start date must be on or before end date.',
      life: 3000,
    });
    return;
  }
  syncActiveFilters();
  loadEntries();
}

function clearFilters() {
  filters.search = '';
  filters.action = null;
  filters.entity_type = null;
  filters.date_from = '';
  filters.date_to = '';
  syncActiveFilters();
  loadEntries();
}

async function exportCsv() {
  exporting.value = true;
  try {
    const rows = [
      ['When', 'Actor', 'Employee', 'Action', 'Field', 'Old value', 'New value'],
    ];
    let offset = 0;
    const batchSize = 200;
    let fetched = 0;
    let exportTotal = total.value || entries.value.length;

    while (fetched < exportTotal) {
      const data = await fetchGlobalAudit({
        ...buildRequestParams(offset),
        limit: batchSize,
        offset,
      });
      if (!data.entries.length) break;
      data.entries.forEach((entry) => {
        rows.push([
          entry.created_at,
          entry.actor_name || 'System',
          entry.target_label || '',
          auditActionLabel(entry.action),
          auditFieldLabel(entry),
          displayAuditValue(entry.old_value),
          displayAuditValue(entry.new_value),
        ]);
      });
      fetched += data.entries.length;
      exportTotal = data.total;
      offset += data.entries.length;
      if (data.entries.length < batchSize) break;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(rows, `change-log-${stamp}.csv`);
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Export failed',
      detail: err?.message || 'Could not export change log.',
      life: 3500,
    });
  } finally {
    exporting.value = false;
  }
}

onMounted(loadEntries);
</script>

<style scoped>
.settings-changelog-results {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
}
</style>
